/**
 * Console strings shared by both consoles.
 *
 * `@cattle/money`'s `en` dictionary covers the trader's phone app. The two web
 * consoles add several hundred strings of their own, and a console string has
 * no business in a bundle shipped to a phone — so they live here and are merged
 * in `lib/i18n.ts`. Same rule either way: no user-facing string is written
 * inline in a component.
 */
export const common = {
  // --- actions ---
  'act.export': 'Export',
  'act.retry': 'Retry',
  'act.close': 'Close',
  'act.confirm': 'Confirm',
  'act.saveChanges': 'Save changes',
  'act.viewAll': 'See all',
  'act.manage': 'Manage',
  'act.open': 'Open',
  'act.reply': 'Reply',
  'act.call': 'Call',
  'act.remind': 'Remind',
  'act.extend': 'Extend',
  'act.notifyOwner': 'Notify owner',
  'act.emailOwner': 'Email owner',
  'act.copy': 'Copy',
  'act.copied': 'Copied',
  'act.dismiss': 'Dismiss',

  // --- table / list chrome ---
  'table.showingOf': 'Showing {shown} of {total}',
  'table.newer': 'Newer',
  'table.older': 'Older',
  'table.sortedBy': 'Sorted by {column}',
  'table.rowActions': 'Actions',
  'table.noResults': 'Nothing matched',

  // --- search & filters ---
  'search.placeholder': 'Search',
  'filter.all': 'All',
  'filter.label': 'Filter',

  // --- the four states every data screen owes the reader ---
  'state.loading': 'Loading…',
  'state.errorTitle': 'That did not load',
  'state.errorBody': 'Something went wrong on our side. Nothing you did caused this.',
  'state.requestId': 'Reference {requestId}',
  'state.emptyTitle': 'Nothing here yet',
  'state.forbiddenTitle': 'Not available to you',
  'state.forbiddenBody': 'Your account does not include this part of the console.',
  // Kept apart from `state.errorTitle` on purpose: a refused write and a dead
  // connection ask different things of whoever is looking at the screen.
  'state.offlineTitle': 'The server could not be reached',

  // --- money & the price lock ---
  // `money.hidden` deliberately absent — @cattle/money already owns it, and the
  // phone app and the consoles must say the same thing about a masked figure.
  'money.locked': 'Prices hidden',
  'money.visibleFor': 'Visible {time}',
  'money.unlock': 'Unlock prices',
  'money.lockNow': 'Hide prices',
  'pin.title': 'Enter your PIN',
  // The window is the tenant's own `priceLock.autoLockSeconds` out of `/me`,
  // not five minutes because the deck said so.
  'pin.subtitle': 'Prices stay visible for {duration}.',
  'pin.wrong': 'That PIN is not right.',
  'pin.cancel': 'Not now',

  // --- confirmation ---
  'confirm.typeToConfirm': 'Type {phrase} to confirm',
  'confirm.typeMismatch': 'That does not match yet.',
  'confirm.irreversible': 'This cannot be undone.',

  // --- danger zone ---
  'danger.title': 'Careful',

  // --- relative time ---
  'time.now': 'Now',
  'time.minutesAgo': '{count} minutes ago',
  'time.hoursAgo': '{count} hours ago',
  'time.hourAgo': 'an hour ago',
  'time.yesterday': 'Yesterday',
  'time.daysAgo': '{count} days ago',
  'time.today': 'Today',
  'time.never': 'Never',

  // --- signing in ---
  'auth.platformTitle': 'Platform Console',
  'auth.platformSub': 'Sign in with your platform account.',
  'auth.ownerTitle': 'Your business',
  'auth.ownerSub': 'Sign in to your own book.',
  'auth.username': 'Username',
  'auth.password': 'Password',
  'auth.businessSlug': 'Business',
  'auth.businessSlugHint': 'The short name in your web address.',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in…',
  'auth.signOut': 'Sign out',
  'auth.checking': 'Checking your session…',
  'auth.toPlatform': 'Platform console',
  'auth.toOwner': 'Business owner console',
  'auth.oneSessionNote':
    'Both consoles share one session on this browser. Signing in here signs you out of the other one.',

  // --- units ---
  'unit.perMonth': '/month',
  'unit.perDay': '/day',
  'unit.days': 'days',
  'unit.animals': 'animals',
  'unit.people': 'people',
  'unit.minutes': 'min',
  'unit.minutesCount': '{count} minutes',
  'unit.secondsCount': '{count} seconds',

  // --- generic labels ---
  'label.status': 'Status',
  'label.business': 'Business',
  'label.owner': 'Owner',
  'label.plan': 'Plan',
  'label.amount': 'Amount',
  'label.when': 'When',
  'label.who': 'Who',
  'label.what': 'What',
  'label.why': 'Why',
  'label.how': 'How',
  'label.name': 'Name',
  'label.date': 'Date',
  'label.storage': 'Storage',
  'label.animals': 'Animals',
  'label.people': 'People',
  'label.role': 'Role',
  'label.lastSignedIn': 'Last signed in',
  'label.username': 'Username',
  'label.do': 'Do',
  'label.for': 'For',
  'label.was': 'Was',
  'label.now': 'Now',
  'label.yes': 'Yes',
  'label.no': 'No',
  'label.on': 'On',
  'label.off': 'Off',
  'label.optional': 'Optional',
  'label.deleted': 'Deleted',

  // --- units and statuses the screens needed once they were built ---
  // A storage figure and its unit are one string, so a translation can move the
  // unit in front of the number. `formatBytes` is the only thing that picks
  // which of the three to use; nothing else should be doing that arithmetic.
  'unit.gb': '{value} GB',
  'unit.mb': '{value} MB',
  'unit.kb': '{value} KB',
  /** The bare unit, for a suffix beside a number the reader is typing. */
  'unit.mbShort': 'MB',
  'unit.perMonthShort': '/mo',

  'invoiceStatus.PAID': 'Paid',
  // The contract's word is ISSUED — raised and not yet settled. "Due" is what
  // that means to whoever is reading the row, so the word stays and the code
  // under it becomes the one the server actually sends.
  'invoiceStatus.ISSUED': 'Due',
  'invoiceStatus.FAILED': 'Failed',
  // A bill withdrawn rather than paid. Never "cancelled" — that is what happens
  // to a subscription, and the two must not read as the same event.
  'invoiceStatus.VOID': 'Voided',
} as const satisfies Record<string, string>
